import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';
import { MessageService } from 'primeng/api';

/**
 * Mirrors the backend `HEARTBEAT_INTERVAL_MS` (project-lock.service.ts). Kept
 * well under the server's 2-min TTL so a single dropped heartbeat never loses
 * the lock.
 */
const HEARTBEAT_INTERVAL_MS = 30_000;

/** Lock row as returned by the backend (dates arrive as ISO strings). */
export interface LockView {
	projectSlug: string;
	userId: string;
	userName: string;
	userEmail: string;
	acquiredAt: string;
	lastHeartbeatAt: string;
	expiresAt: string;
	isStale: boolean;
}

interface LockResponse {
	lock: LockView | null;
	isMine: boolean;
	/** Present for imported projects — already read-only, no lock is taken. */
	readOnly?: string;
}

/**
 * Owns the editor's edit-lock for one project (spec/plans/edit-locks/). Acquires
 * on open, heartbeats while held, releases on leave, and exposes `lockedByOther`
 * — the signal `EditorStateService.isReadOnly()` ORs in so every read-only
 * affordance (forms, timeline, file-browser, Monaco) turns off for the second
 * user with no per-component work.
 *
 * Provided at the editor component level (same scope as EditorStateService), so
 * its `DestroyRef`-bound heartbeat stops when the editor is torn down.
 */
@Injectable()
export class EditLockService {
	private http = inject(HttpClient);
	private auth = inject(AuthService);
	private messages = inject(MessageService);
	private destroyRef = inject(DestroyRef);

	private slug = '';
	private pollStarted = false;

	readonly lock = signal<LockView | null>(null);
	readonly isMine = signal(false);
	/** True when a *different* user holds a live lock → editor is read-only. */
	readonly lockedByOther = signal(false);
	readonly holderName = computed(() => this.lock()?.userName ?? '');
	readonly isStale = computed(() => this.lock()?.isStale ?? false);

	/**
	 * Whether the current user may take over the lock: a stale lock is takeable
	 * by anyone, a live foreign lock only by an admin (the backend enforces the
	 * same rule — this just governs whether the button is shown).
	 */
	readonly canTakeOver = computed(() => {
		if (!this.lockedByOther()) return false;
		if (this.isStale()) return true;
		return this.auth.user()?.isAdmin === true;
	});

	private url(slug: string): string {
		return `${environment.apiUrl}/api/projects/${slug}/lock`;
	}

	/**
	 * Acquire the lock when the editor opens `slug`, then keep polling. The poll
	 * runs whether or not we got the lock: as the holder each tick is a heartbeat;
	 * as a blocked second user each tick retries the acquire, so we pick the lock
	 * up automatically within one interval once the holder leaves — no refresh
	 * needed.
	 */
	acquire(slug: string): void {
		this.slug = slug;
		this.requestLock();
		this.startPolling();
	}

	/** Force-take the lock — for the banner's Take-over button (stale, or admin). */
	takeOver(): void {
		if (!this.slug) return;
		this.http.put<LockResponse>(`${this.url(this.slug)}?force=1`, {}).subscribe({
			next: (res) => {
				if (res.isMine) {
					this.applyMine(res);
					this.messages.add({ severity: 'success', summary: 'Lock taken over', detail: 'You are now editing this project.' });
				} else {
					this.applyBlocked(res.lock);
				}
			},
			error: (err: HttpErrorResponse) => {
				const detail = err.status === 403 ? 'Only an admin can take over an active lock.' : 'Could not take over the lock.';
				this.messages.add({ severity: 'error', summary: 'Take-over failed', detail });
			},
		});
	}

	/** Release the lock on leaving the editor (SPA navigation / ngOnDestroy). */
	release(): void {
		if (!this.slug || !this.isMine()) return;
		this.http.delete(this.url(this.slug)).subscribe({ error: () => { /* TTL cleans up */ } });
	}

	/**
	 * Best-effort release on full page unload (tab close / reload). `fetch` with
	 * `keepalive` lets the bearer header ride an unload request (sendBeacon can't
	 * set headers); the ~2-min server TTL is the guarantee if it doesn't land.
	 */
	releaseBeacon(): void {
		if (!this.slug || !this.isMine()) return;
		const token = this.auth.accessToken();
		try {
			void fetch(this.url(this.slug), {
				method: 'DELETE',
				keepalive: true,
				headers: token ? { Authorization: `Bearer ${token}` } : {},
			});
		} catch { /* nothing more we can do during unload */ }
	}

	/**
	 * Called by `EditorDataService` when a save is rejected with 423 — the fast
	 * path for reflecting a takeover while the user is actively editing (the poll
	 * is the ≤30s backstop for an idle user).
	 */
	notifyLockLost(lock: LockView | null): void {
		this.applyBlocked(lock);
	}

	/** One acquire/heartbeat/retry round-trip; applies whatever the server reports. */
	private requestLock(): void {
		if (!this.slug) return;
		this.http.put<LockResponse>(this.url(this.slug), {}).subscribe({
			next: (res) => {
				if (res.isMine) this.applyMine(res);
				// `lock: null` here means imported (read-only handled elsewhere), not blocked.
				else this.applyBlocked(res.lock);
			},
			error: (err: HttpErrorResponse) => {
				if (err.status === 423) this.applyBlocked((err.error as { lock?: LockView } | null)?.lock ?? null);
				// Other errors (network): leave state as-is; the next tick retries.
			},
		});
	}

	private startPolling(): void {
		if (this.pollStarted) return;
		this.pollStarted = true;
		interval(HEARTBEAT_INTERVAL_MS)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.requestLock());
	}

	/** We hold the lock (just acquired, or refreshed by a heartbeat). */
	private applyMine(res: LockResponse): void {
		this.lock.set(res.lock);
		this.isMine.set(true);
		this.lockedByOther.set(false);
	}

	/**
	 * Someone else holds the lock (or it's free/imported → `lock` null). Flips the
	 * editor read-only; the poll keeps running so a later free lock is picked up
	 * automatically. Toasts once, only on the transition from holding it (a
	 * takeover), not on every poll tick or on opening into an already-locked
	 * project.
	 */
	private applyBlocked(lock: LockView | null): void {
		const wasMine = this.isMine();
		this.lock.set(lock);
		this.isMine.set(false);
		this.lockedByOther.set(!!lock);
		if (wasMine && lock) {
			this.messages.add({
				severity: 'warn',
				summary: `${lock.userName} took over editing`,
				detail: 'This project is now read-only. Unsaved changes may not have been saved.',
				life: 10_000,
			});
		}
	}
}
